import { formatDistanceToNow } from "date-fns";

/**
 * Universal safe date parser for all browsers (especially iOS WebKit / Safari old & latest).
 * Safari fails on MySQL datetime format "YYYY-MM-DD HH:mm:ss".
 * This converts spaces to "T" and provides fallbacks so it never returns an unhandled Invalid Date.
 */
export function parseSafeDate(input) {
  if (!input) return null;
  if (input instanceof Date) {
    return isNaN(input.getTime()) ? null : input;
  }
  if (typeof input === "number") {
    const d = new Date(input);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof input !== "string") return null;

  const trimmed = input.trim();
  if (!trimmed) return null;

  // 1. Replace space between date and time with 'T' (e.g. "2024-03-25 15:30:00" -> "2024-03-25T15:30:00")
  let normalized = trimmed;
  if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(normalized)) {
    normalized = normalized.replace(/^(\d{4}-\d{2}-\d{2})\s+/, "$1T");
  }

  let d = new Date(normalized);
  if (!isNaN(d.getTime())) {
    return d;
  }

  // 2. Fallback for older Safari: replace hyphens with slashes
  try {
    const slashVersion = normalized.replace(/-/g, "/").replace("T", " ");
    d = new Date(slashVersion);
    if (!isNaN(d.getTime())) {
      return d;
    }
  } catch (_e) {
    /* ignore fallback parse error */
  }

  return null;
}

/**
 * Safe wrapper around date-fns formatDistanceToNow.
 * Guarantees NO uncaught RangeError on iOS when date is invalid or undefined.
 */
export function safeFormatDistanceToNow(input, options = { addSuffix: true }, fallback = "recently") {
  const d = parseSafeDate(input);
  if (!d) return fallback;
  try {
    return formatDistanceToNow(d, options);
  } catch (_e) {
    return fallback;
  }
}

/**
 * Clean numeric date format "DD/MM/YYYY" without relying on Intl/locale quirks.
 */
export function safeFormatDate(input, fallback = "N/A") {
  const d = parseSafeDate(input);
  if (!d) return fallback;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Formats date and time e.g. "25 Mar 2024, 03:30 PM".
 * Works 100% across all old and latest iOS/Android devices without locale errors.
 */
export function safeFormatDateTime(input, fallback = "-") {
  const d = parseSafeDate(input);
  if (!d) return fallback;

  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];
  const day = String(d.getDate()).padStart(2, "0");
  const month = months[d.getMonth()];
  const year = d.getFullYear();

  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12; // '0' should be '12'
  const strHours = String(hours).padStart(2, "0");

  return `${day} ${month} ${year}, ${strHours}:${minutes} ${ampm}`;
}

/**
 * Safe toLocaleDateString with automatic fallback if the requested locale
 * (like 'en-PK' on old iOS) throws RangeError: Invalid language tag.
 */
export function safeLocaleDate(input, locale = "en-US", options, fallback = "-") {
  const d = parseSafeDate(input);
  if (!d) return fallback;
  try {
    return d.toLocaleDateString(locale, options);
  } catch (_e) {
    try {
      return d.toLocaleDateString("en-US", options);
    } catch (_e2) {
      return safeFormatDate(d, fallback);
    }
  }
}
